// packages/app/src/app/shared/campaign-management/CampaignDetails.tsx
"use client";

import React from "react";
import { useParams, Link } from "react-router-dom";
import { Text, Input } from "rizzui";
import FormGroup from "../../../../shared/form-group";
import { IoIosArrowRoundBack } from "react-icons/io";
import { useCampaign } from "../../../../../hooks/use-campaign"; // you'll need to create this
import { useUser } from "../../../../../hooks/use-user";

export default function CampaignDetails() {
  const { id } = useParams<{ id: string }>();
  const {
    data: campaign,
    isLoading: isCampLoading,
    error: campError,
  } = useCampaign(id!);

  const ownerId = campaign?.user?._id;
  const {
    data: owner,
    isLoading: isOwnerLoading,
    error: ownerError,
  } = useUser(ownerId ?? "");

  if (isCampLoading) return <div>Loading campaign…</div>;
  if (campError)
    return <div className="text-red-600">Error: {campError.message}</div>;
  if (!campaign) return <div>Campaign not found.</div>;

  function formatCurrency(amount: number, code: string) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: code,
        minimumFractionDigits: 2,
      }).format(amount);
    } catch {
      // Non-ISO codes (like USDT) will land here:
      return `${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })} ${code}`;
    }
  }

  return (
    <div className="@container">
      {/* back button */}
      <Link
        to="/campaign/all-campaign"
        className="flex gap-1 items-center w-fit mb-5 px-3 py-2 border rounded-full"
      >
        <IoIosArrowRoundBack />
        <Text>All Campaigns</Text>
      </Link>

      {/* Campaign Info header */}
      <FormGroup
        title="Campaign Info"
        description="View detailed information about this campaign."
        className="pt-7 @2xl:pt-9 @3xl:grid-cols-12 @3xl:pt-11"
      />

      <div className="mb-10 grid gap-7 divide-y divide-dashed divide-gray-200">
        <FormGroup title="Title">
          <Input value={campaign.title} readOnly className="col-span-full" />
        </FormGroup>

        <FormGroup title="Reason">
          <Input value={campaign.reason} readOnly className="col-span-full" />
        </FormGroup>

        <FormGroup title="City / Country">
          <Input
            value={`${campaign.city}, ${campaign.country}`}
            readOnly
            className="col-span-full"
          />
        </FormGroup>

        <FormGroup title="Fund For">
          <Input value={campaign.fundFor} readOnly className="col-span-full" />
        </FormGroup>

        <FormGroup title="Visibility">
          <Input
            value={campaign.visibility}
            readOnly
            className="col-span-full"
          />
        </FormGroup>

        <FormGroup title="Currency">
          <Input
            value={campaign.currencyType}
            readOnly
            className="col-span-full"
          />
        </FormGroup>

        <FormGroup title="Amount Needed">
          <Input
            value={formatCurrency(campaign.amountNeeded, campaign.currencyType)}
            readOnly
            className="col-span-full"
          />
        </FormGroup>

        <FormGroup title="Amount Raised">
          <Input
            value={formatCurrency(campaign.amountRaised, campaign.currencyType)}
            readOnly
            className="col-span-full"
          />
        </FormGroup>

        <FormGroup title="Available Balance">
          <Input
            value={formatCurrency(
              campaign.availableBalance,
              campaign.currencyType
            )}
            readOnly
            className="col-span-full"
          />
        </FormGroup>

        <FormGroup title="Total Withdrawn">
          <Input
            value={formatCurrency(
              campaign.totalWithdrawn,
              campaign.currencyType
            )}
            readOnly
            className="col-span-full"
          />
        </FormGroup>

        <FormGroup title="Created At">
          <Input
            value={new Date(campaign.createdAt).toLocaleString()}
            readOnly
            className="col-span-full"
          />
        </FormGroup>

        <FormGroup title="Image">
          <img
            src={campaign.campaignImage}
            alt={campaign.title}
            className="max-h-48 object-contain"
          />
        </FormGroup>

        <FormGroup title="Story">
          {/* sanitize or transform as needed */}
          <div
            className="prose max-w-none"
            dangerouslySetInnerHTML={{ __html: campaign.story }}
          />
        </FormGroup>
      </div>

      {/* Owner Info */}
      <FormGroup
        title="Campaign Owner"
        description="Details about the user who created this campaign."
        className="pt-7 @2xl:pt-9"
      />

      {isOwnerLoading ? (
        <div>Loading owner…</div>
      ) : ownerError ? (
        <div className="text-red-600">Error: {ownerError.message}</div>
      ) : (
        owner && (
          <div className="grid grid-cols-1 gap-7 divide-y divide-dashed divide-gray-200">
            <FormGroup title="Name">
              <Input
                value={`${owner.firstName} ${owner.middleName ?? ""} ${owner.lastName}`}
                readOnly
                className="col-span-full"
              />
            </FormGroup>
            <FormGroup title="Email">
              <Input value={owner.email} readOnly className="col-span-full" />
            </FormGroup>
            <FormGroup title="Account Type">
              <Input
                value={owner.accountType}
                readOnly
                className="col-span-full"
              />
            </FormGroup>
            <FormGroup title="Phone">
              <Input
                value={String(owner.phoneNumber)}
                readOnly
                className="col-span-full"
              />
            </FormGroup>
            <FormGroup title="KYC Status">
              <Input
                value={owner.kycVerified}
                readOnly
                className="col-span-full"
              />
            </FormGroup>
          </div>
        )
      )}
    </div>
  );
}
